// View: WorkcenterShiftpatternView.tsx
// This version is the same as the one provided two steps ago,
// with the ESLint fix (curly braces in switch cases).

/* eslint-disable sort-imports */
/* eslint-disable simple-import-sort/imports */
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box, Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from "@mui/material";
import React, { useCallback, useMemo, type HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { Virtuoso } from "react-virtuoso";

import WorkerRow from "./worker-row";
import { CalendarHeader } from "../calendar-header";
import { format, isPast } from "date-fns";
import ShiftPatternSummary from "./shift-pattern-summary";
import addDays from "date-fns/addDays";
import useUserData from "../../../../components/drawer/ui/use-user-data";
import { useSelector } from "react-redux";
import useWorkerSchedulerData from "../../controllers/use-worker-scheduler-data";
import { useDateFnsLocale } from "../../../../utils/date-locale";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import GroupIcon from "@mui/icons-material/Group";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const BORDER_COLOR = "#E5E5E5";

// Interface definitions remain the same
interface WorkerShift {
  workerId: string;
  badgeNumber: number | null;
  primaryJobName: string;
  workerName: string;
  firstName: string;
  lastName: string;
  shifts: any[];
  dPrimaryJobId: number; // Worker's default job
  companyEmployeeId: string;
  dwCenterId: number; // Worker's default workcenter
  primaryJobId: number; // Worker's current primary job
  patternId: number; // Worker's default pattern
  uPrimaryJobId: number; // This is the shift's primary job ID
  workerTypeId: string;
  workerSubTypeId: string;
  // Flags added by controller
  isWorkcenterChanged?: boolean;
  isJobChanged?: boolean;
  isPatternChanged?: boolean;
  workCenterId?: number; // Added this based on original code, might not be needed if dwCenterId is correct default
}

interface ShiftPattern {
  id: number;
  workers: WorkerShift[];
}

interface Workcenter {
  id: number;
  name: string;
  totalWorkers: number;
  shiftPatterns: ShiftPattern[];
  unassignedWorkers: WorkerShift[];
  flexWorkers: WorkerShift[];
}

interface VirtualizedItem {
  type: "workcenter" | "shiftpattern" | "worker" | "no-workers" | "jobgroup";
  id: string;
  data: any;
  level: number;
  workcenterId?: number;
  shiftPatternId?: number;
  jobId?: number | null;
}

interface WorkcenterShiftpatternViewProps {
  workerWorkcenterShiftPatternsMap: Workcenter[];
  schedulerContainerHeight: number;
  isWorkcenterOpen: (workcenterId: number) => boolean;
  handleWorkcenterToggle: (workcenterId: number) => void;
  shiftPatternsObj: any;
  isShiftPatternOpen: (shiftPatternId: string) => boolean;
  handleShiftPatternToggle: (shiftPatternId: string) => void;
  selectedLocation: number | null;
  isAppliedFilterInView: boolean;
  isPrinting?: boolean;
  startingDay?: Date;
}

interface ScrollerProps extends HTMLAttributes<HTMLDivElement> {
  style?: React.CSSProperties;
}

const VirtuosoScroller = React.forwardRef<HTMLDivElement, ScrollerProps>(
  (props, ref) => (
    <div
      {...props}
      ref={ref}
      style={{
        ...(props.style || {}),
        overflow: "auto",
        height: "100%"
      }}
    />
  )
);
VirtuosoScroller.displayName = "VirtuosoScroller";

const WorkcenterShiftpatternView: React.FC<WorkcenterShiftpatternViewProps> = ({
                                                                                 workerWorkcenterShiftPatternsMap,
                                                                                 schedulerContainerHeight,
                                                                                 isWorkcenterOpen,
                                                                                 handleWorkcenterToggle,
                                                                                 shiftPatternsObj,
                                                                                 isShiftPatternOpen,
                                                                                 handleShiftPatternToggle,
                                                                                 selectedLocation,
                                                                                 isAppliedFilterInView = false,
                                                                                 isPrinting = false,
                                                                                 startingDay = new Date()
                                                                               }) => {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
  const {
    updateAvailabilityStatusStatus,
    filterData: { allJobsObj, schedulingStatusesObj }
  } = useWorkerSchedulerData();
  const allLocations = useSelector(
    (state: any) => state.fullTimeShifts?.filterData?.allLocations
  );
  const selectedLocationName = useMemo(() => {
    if (selectedLocation === null) return "";
    const location = allLocations.find(
      (loc: { id: number }) => loc.id === selectedLocation
    );
    return location ? location.name : "";
  }, [selectedLocation, allLocations]);
  const { company, firstName, lastName } = useUserData();
  const workcenters = workerWorkcenterShiftPatternsMap;

  // Simplified getShiftPatterns - relies on controller's pre-processing
  const getShiftPatterns = (
    shiftPatterns: any[],
    shiftPatternsObj: { [x: string]: { name: any } },
  ) => {
    return shiftPatterns.map((pattern: any) => {
      const shiftsByDate: Record<string, { start: string; end: string }[]> = {};

      pattern.workers?.forEach((worker: any) => {
        // Trust worker.shifts from controller
        worker.shifts?.forEach((shift: any) => {
          const dateKey = format(new Date(shift.startDateTime), "yyyy-MM-dd");
          const start = shift.startDateTime;
          const end = shift.endDateTime;

          if (!shiftsByDate[dateKey]) {
            shiftsByDate[dateKey] = [];
          }

          const isDuplicate = shiftsByDate[dateKey].some(
            (s) => s.start === start && s.end === end
          );

          // No need to check patternId again if controller does it
          if (!isDuplicate && !shift.isFlexShift) {
            shiftsByDate[dateKey].push({ start, end });
          }
        });
      });

      return {
        id: pattern.id,
        name: shiftPatternsObj?.[pattern.id]?.name ?? "-",
        shifts: shiftsByDate
      };
    });
  };

  // Flatten the hierarchical structure into a virtualized list
  const virtualizedItems = useMemo(() => {
    const items: VirtualizedItem[] = [];

    /**
     * Helper to group workers by job.
     * Places the worker object under the appropriate job context for this row.
     * Default entries go under the worker's default job.
     * Clones go under the job associated with the clone's filtered shifts.
     */
    const groupWorkersByJob = (workers: WorkerShift[]) => {
      const groupedWorkers = new Map<number | null, WorkerShift[]>();

      workers.forEach((worker) => {
        const relevantShifts = worker.shifts || [];

        // Heuristic to identify default entry vs. clone:
        // Default entries should have all flags as false.
        // Clones will have at least one flag as true.
        const isDefaultEntry = !worker.isWorkcenterChanged && !worker.isJobChanged && !worker.isPatternChanged;

        let targetJobId: number | null;

        if (isDefaultEntry) {
          // Default entry: Group under the worker's default primary job ID
          targetJobId = worker.primaryJobId || null; // Use worker's default job from worker object
        } else {
          // Clone entry: Group under the job ID associated with its specific (moved) shifts.
          // Since clones for a specific move context only contain shifts for THAT job,
          // we can reliably take the job ID from the first shift in the list.
          targetJobId = relevantShifts.length > 0
            ? (relevantShifts[0].uPrimaryJobId || null)
            : (worker.primaryJobId || null); // Fallback if clone somehow has no shifts
        }


        if (!groupedWorkers.has(targetJobId)) {
          groupedWorkers.set(targetJobId, []);
        }
        // Add the entire worker object (default or clone) to the determined job group
        groupedWorkers.get(targetJobId)!.push(worker);
      });

      return groupedWorkers;
    };

    /**
     * Helper function to add the grouped job/worker items to the virtualized list.
     */
    const addGroupedItemsToList = (
      groupedWorkers: Map<number | null, any[]>,
      baseId: string, // Unique prefix for IDs
      level: number,
      workcenterId: number,
      shiftPatternId: number | null // Allow null for unassigned/flex
    ) => {
      const items: VirtualizedItem[] = [];
      const jobIds = Array.from(groupedWorkers.keys()).filter(
        (id) => id !== null
      ) as number[];

      // Sort by job name
      jobIds.sort((a, b) =>
        (allJobsObj[a]?.name || `Job ${a}`).localeCompare(
          allJobsObj[b]?.name || `Job ${b}`
        )
      );

      // Add sorted job groups
      jobIds.forEach((jobId) => {
        const workersInGroup = groupedWorkers.get(jobId)?.sort((a: WorkerShift, b: WorkerShift) => {
          const hasBadgeA = a.badgeNumber !== undefined && a.badgeNumber !== null;
          const hasBadgeB = b.badgeNumber !== undefined && b.badgeNumber !== null;
          const hasEmpIdA = a.companyEmployeeId !== undefined && a.companyEmployeeId !== null;
          const hasEmpIdB = b.companyEmployeeId !== undefined && b.companyEmployeeId !== null;

          // Assign badge numbers beforehand
          const badgeA = a.badgeNumber;
          const badgeB = b.badgeNumber;

          // 1. Prioritize workers with badgeNumber
          if (hasBadgeA && !hasBadgeB) {
            return -1; // a comes first
          }
          if (!hasBadgeA && hasBadgeB) {
            return 1; // b comes first
          }

          // 2. If both have or both lack badgeNumber, compare badgeNumbers if they exist
          if (hasBadgeA && hasBadgeB) {
            // Now use the pre-assigned badgeA and badgeB
            const comparison = typeof badgeA === "number" && typeof badgeB === "number"
              ? badgeA - badgeB
              : String(badgeA).localeCompare(String(badgeB));
            if (comparison !== 0) {
              return comparison; // Sort by badgeNumber if different
            }
            // If badgeNumbers are the same, fall through to compare empId
          }

          // At this point, either both have the same badgeNumber, or neither has a badgeNumber.
          // Now prioritize by companyEmployeeId presence.
          if (hasEmpIdA && !hasEmpIdB) {
            return -1; // a comes first
          }
          if (!hasEmpIdA && hasEmpIdB) {
            return 1; // b comes first
          }

          // 3. If both have or both lack companyEmployeeId (and badgeNumbers were inconclusive),
          // compare companyEmployeeIds if they exist
          if (hasEmpIdA && hasEmpIdB) {
            return String(a.companyEmployeeId).localeCompare(String(b.companyEmployeeId));
          }

          // 4. If neither has badgeNumber nor companyEmployeeId, consider them equal
          return 0;
        });
        const jobName = allJobsObj[jobId]?.name || `Job ${jobId}`;
        const jobGroupId = `${baseId}-${jobId}`;
        items.push({
          type: "jobgroup",
          id: jobGroupId,
          data: { id: jobId, name: jobName, workers: workersInGroup },
          level,
          workcenterId,
          shiftPatternId: shiftPatternId ?? undefined, // Pass undefined if null
          jobId
        });

        // Add workers for this job group
        workersInGroup?.forEach((worker) => {
          // Use a simpler unique key incorporating context
          const uniqueWorkerKey = `worker-${workcenterId}-${
            shiftPatternId ?? "unkn"
          }-${jobId}-${worker.workerId}`;
          items.push({
            type: "worker",
            id: uniqueWorkerKey, // Ensure unique ID across different contexts
            data: worker, // This worker object now has the correctly scoped shifts from controller
            level: level + 1,
            workcenterId,
            shiftPatternId: shiftPatternId ?? undefined,
            jobId
          });
        });
      });

      // Add "Unassigned" group at the end
      if (groupedWorkers.has(null)) {
        const workersInGroup = groupedWorkers.get(null)!;
        const jobGroupId = `${baseId}-unassigned`;
        items.push({
          type: "jobgroup",
          id: jobGroupId,
          data: { id: null, name: "Unassigned", workers: workersInGroup },
          level,
          workcenterId,
          shiftPatternId: shiftPatternId ?? undefined,
          jobId: null
        });

        // Add workers for "Unassigned"
        workersInGroup.forEach((worker) => {
          const uniqueWorkerKey = `worker-${workcenterId}-${
            shiftPatternId ?? "unkn"
          }-unassigned-${worker.workerId}`;
          items.push({
            type: "worker",
            id: uniqueWorkerKey,
            data: worker,
            level: level + 1,
            workcenterId,
            shiftPatternId: shiftPatternId ?? undefined,
            jobId: null
          });
        });
      }
      return items;
    };

    // --- Main List Generation ---
    workcenters.forEach((workcenter) => {
      // Add workcenter item
      items.push({
        type: "workcenter",
        id: `workcenter-${workcenter.id}`,
        data: workcenter,
        level: 0,
        workcenterId: workcenter.id
      });

      // If workcenter is open, add its content
      if (isWorkcenterOpen(workcenter.id)) {
        const hasWorkers = workcenter.totalWorkers > 0; // Trust controller's count

        if (!hasWorkers) {
          // Add "No workers found" item
          items.push({
            type: "no-workers",
            id: `no-workers-${workcenter.id}`,
            data: { workcenterId: workcenter.id },
            level: 1,
            workcenterId: workcenter.id
          });
        } else {
          // --- 1. Regular Shift Patterns ---
          if (workcenter.shiftPatterns && workcenter.shiftPatterns.length > 0) {
            workcenter.shiftPatterns.forEach((shiftPattern) => {
              // Only add pattern row if it actually has workers (controller provides this)
              if (shiftPattern.workers && shiftPattern.workers.length > 0) {
                const uniqueItemId = `shiftpattern-${workcenter.id}-${shiftPattern.id}`;
                items.push({
                  type: "shiftpattern",
                  id: uniqueItemId,
                  data: shiftPattern,
                  level: 1,
                  workcenterId: workcenter.id,
                  shiftPatternId: shiftPattern.id
                });

                if (isShiftPatternOpen(uniqueItemId)) {
                  // Use updated groupWorkersByJob
                  const groupedWorkers = groupWorkersByJob(
                    shiftPattern.workers
                  );
                  const newItems = addGroupedItemsToList(
                    groupedWorkers,
                    `job-${workcenter.id}-${shiftPattern.id}`,
                    2,
                    workcenter.id,
                    shiftPattern.id
                  );
                  items.push(...newItems);
                }
              }
            });
          }

          // --- 2. Unassigned Workers Section ---
          if (workcenter.unassignedWorkers.length > 0) {
            const unassignedShiftPatternId =
              workcenter.id > 0 ? -1 * workcenter.id : workcenter.id - 1; // Handle potential negative WC ID
            const uniqueItemId = `shiftpattern-${workcenter.id}-unassigned`; // <-- Get unique string ID
            items.push({
              type: "shiftpattern",
              id: uniqueItemId,
              data: {
                id: unassignedShiftPatternId,
                workers: workcenter.unassignedWorkers,
                isUnassigned: true
              },
              level: 1,
              workcenterId: workcenter.id,
              shiftPatternId: unassignedShiftPatternId
            });

            if (isShiftPatternOpen(uniqueItemId)) {
              // Use updated groupWorkersByJob
              const groupedWorkers = groupWorkersByJob(
                workcenter.unassignedWorkers
              );
              const newItems = addGroupedItemsToList(
                groupedWorkers,
                `job-${workcenter.id}-unassigned`,
                2,
                workcenter.id,
                unassignedShiftPatternId // Pass the negative ID
              );
              items.push(...newItems);
            }
          }

          // --- 3. Flex Workers Section ---
          if (workcenter.flexWorkers.length > 0 && selectedLocation) {
            const flexShiftPatternId =
              workcenter.id > 0
                ? -1 * workcenter.id * 1000
                : workcenter.id * 1000 - 1; // Handle potential negative WC ID
            const uniqueItemId = `shiftpattern-${workcenter.id}-flex`; // <-- Get unique string ID
            items.push({
              type: "shiftpattern",
              id: uniqueItemId,
              data: {
                id: flexShiftPatternId,
                workers: workcenter.flexWorkers,
                isFlex: true
              },
              level: 1,
              workcenterId: workcenter.id,
              shiftPatternId: flexShiftPatternId
            });

            if (isShiftPatternOpen(uniqueItemId)) {
              // Use updated groupWorkersByJob
              const groupedWorkers = groupWorkersByJob(workcenter.flexWorkers);
              const newItems = addGroupedItemsToList(
                groupedWorkers,
                `job-${workcenter.id}-flex`,
                2,
                workcenter.id,
                flexShiftPatternId // Pass the negative ID
              );
              items.push(...newItems);
            }
          }
        }
      }
    });

    return items;
  }, [
    workcenters,
    isWorkcenterOpen,
    isShiftPatternOpen,
    selectedLocation,
    allJobsObj
  ]);

  // --- renderItem function remains the same ---
  const renderItem = useCallback(
    (index: number) => {
      const item = virtualizedItems[index];
      let lastWorkerRow = false;
      let justAfterWorkcenterRow = false;
      let lastWorkcenterName = "";
      let lastWorkcenterWorkersCount = 0;
      let isFirstWorkcenterWithWorkers = false;

      // ... (rest of the checks remain the same) ...
      // Check if the item is the first workcenter having more than 0 worker
      if (item.type === "workcenter" && item.data.totalWorkers > 0) {
        // Check in past if we have work center with workers
        isFirstWorkcenterWithWorkers = !virtualizedItems
          .slice(0, index)
          .some((i) => i.type === "workcenter" && i.data.totalWorkers > 0);
      }

      // Check if next item is shift pattern and current item is a worker
      if (item.type === "worker" && index < virtualizedItems.length - 1) {
        const nextItem = virtualizedItems[index + 1];
        if (nextItem.type === "shiftpattern" || nextItem.type === "jobgroup") {
          // Check for job group too
          // Check if the *next* worker item belongs to the same parent
          let nextWorkerIndex = index + 1;
          while (
            nextWorkerIndex < virtualizedItems.length &&
            virtualizedItems[nextWorkerIndex].type !== "worker"
            ) {
            nextWorkerIndex++;
          }
          if (nextWorkerIndex < virtualizedItems.length) {
            const nextWorkerItem = virtualizedItems[nextWorkerIndex];
            // Compare parentage (WC, SP, Job)
            if (
              nextWorkerItem.workcenterId !== item.workcenterId ||
              nextWorkerItem.shiftPatternId !== item.shiftPatternId ||
              nextWorkerItem.jobId !== item.jobId
            ) {
              lastWorkerRow = true; // This is the last worker in the current group
            }
          } else {
            lastWorkerRow = true; // This is the last worker overall
          }
        }
      }
      // Check if current item is a workcenter and next item is a worker
      if (item.type === "shiftpattern" && index - 1 >= 0) {
        const previousItem = virtualizedItems[index - 1];
        if (previousItem.type === "workcenter") {
          justAfterWorkcenterRow = true;
        }
      }

      // If the item type is shiftpattern, find the last workcenter name and workers count
      if (item.type === "shiftpattern") {
        // Look for the last workcenter before this shift pattern
        for (let i = index - 1; i >= 0; i--) {
          const previousItem = virtualizedItems[i];
          if (previousItem.type === "workcenter") {
            const workcenter = previousItem.data as Workcenter;
            lastWorkcenterName = workcenter.name;
            lastWorkcenterWorkersCount = workcenter.totalWorkers;
            break; // Stop once we find the last workcenter
          }
        }
      }

      switch (item.type) {
        case "workcenter": {
          if (item.data.totalWorkers === 0 && isPrinting) {
            return null;
          }
          return (
            <Box className={isFirstWorkcenterWithWorkers ? "" : "page-break"}>
              {isPrinting && (
                <>
                  {isFirstWorkcenterWithWorkers && (
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "20px",
                        lineHeight: "28px",
                        fontWeight: 500,
                        color: "rgba(0, 0, 0, 0.87)",
                        marginBottom: "16px",
                        marginTop: "16px",
                        marginLeft: "8px",
                        textAlign: "center",
                        width: "100%"
                      }}
                    >
                      {t("full-time-worker-scheduler_wilya-schedule-for", {
                        company,
                        location: selectedLocationName,
                      })}
                    </Typography>
                  )}
                  {isFirstWorkcenterWithWorkers && (
                    <Box sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      mb: 2
                    }}>
                      {/* Scope of Movement Legend */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5, fontSize: "0.8rem" }}>
                          {t("full-time-worker-scheduler_scope-of-movement-legend")}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", alignItems: "center" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <FactoryOutlinedIcon sx={{ fontSize: "1.1rem", color: "rgba(0,0,0,0.87)" }} />
                            <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.87)", ml: 0.5 }}>{t("full-time-worker-scheduler_work-center")}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <GroupIcon sx={{ fontSize: "1.1rem", color: "rgba(0,0,0,0.87)" }} />
                            <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.87)", ml: 0.5}}>{t("full-time-worker-scheduler_shift-pattern")}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <WorkHistoryIcon sx={{ fontSize: "1.1rem", color: "rgba(0,0,0,0.87)" }} />
                            <Typography variant="caption"
                                        sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.87)", ml: 0.5 }}>{t("full-time-worker-scheduler_jobs")}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      {/* Status Legend */}
                      <Box> {/* Margin below status legend section */}
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5, fontSize: "0.8rem" }}>
                          {t("full-time-worker-scheduler_status-legend")}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", alignItems: "center" }}>
                          {Object.values(schedulingStatusesObj)
                            ?.filter(status => status.code !== "RAS") // Exclude RAS
                            ?.map((status) => (
                            <Box key={status.id} sx={{ display: "flex", alignItems: "center" }}>
                              <Chip
                                label={status.code === "COF" ? "TO" : status.code}
                                size='small'
                                deleteIcon={<></>}
                                sx={{
                                  backgroundColor: status?.backgroundColor,
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    color: "rgba(0,0,0,0.87)",
                                    fontFamily: "Roboto",
                                    fontSize: "12px",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "16px",
                                    letterSpacing: "0.16px",
                                    padding: "0px 4px",
                                  },
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.87)", ml: 0.5 }}>
                                {status.name === "Called Off"
                                  ? t("full-time-worker-scheduler_time-off")
                                  : status.name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%"
                  }}>
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        width: "100%",
                        height: "36px",
                        maxHeight: "36px",
                        backgroundColor: "#e3e6ed",
                        borderLeft: `1px solid ${BORDER_COLOR}`,
                        borderRight: `1px solid ${BORDER_COLOR}`
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleWorkcenterToggle(item.data.id)}
                        sx={{
                          width: "100%",
                          height: "36px",
                          maxHeight: "36px",
                          gap: 0.5,
                          backgroundColor: "#e3e6ed"
                        }}
                      >
                        <ListItemText>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 1,
                              alignItems: "center"
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: "14px",
                                lineHeight: "18px",
                                fontWeight: 500,
                                letterSpacing: "0.15px",
                                color: "rgba(0, 0, 0, 0.87)"
                              }}
                            >
                              {item.data.name}
                            </Typography>
                            <Typography
                              component="label"
                              sx={{
                                color: "rgba(0, 0, 0, 0.87)",
                                fontSize: isPrinting ? "12px" : "13px",
                                fontWeight: "400",
                                lineHeight: "18px",
                                letterSpacing: "0.16px",
                                marginTop: isPrinting ? "2.5px" : "4px"
                              }}
                            >
                              {isPrinting
                                ? t(
                                    item.data.totalWorkers === 1
                                      ? "full-time-worker-scheduler_employee-count_singular"
                                      : "full-time-worker-scheduler_employee-count",
                                    { count: item.data.totalWorkers }
                                  )
                                : t(
                                    item.data.totalWorkers === 1
                                      ? "full-time-worker-scheduler_worker-count_singular"
                                      : "full-time-worker-scheduler_worker-count",
                                    { count: item.data.totalWorkers }
                                  )}
                            </Typography>
                          </Box>
                        </ListItemText>
                      </ListItemButton>
                    </List>
                  </Box>
                  <CalendarHeader
                    isPrinting={isPrinting}
                    datesWidthPercentage={50}
                    title={<Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "rgba(0,0,0,0.6)"
                      }}
                    >
                      {t("full-time-worker-scheduler_week-of", {
                        start: format(startingDay, "M/d/yyyy"),
                        end: format(addDays(startingDay, 6), "M/d/yyyy"),
                      })}
                    </Typography>}
                    width={"100%"}
                    borderLess={true}
                  />
                  <ShiftPatternSummary
                    shiftPatterns={getShiftPatterns(
                      item.data.shiftPatterns,
                      shiftPatternsObj,
                    )}
                    width={"100%"}
                  />
                  {!isPrinting &&
                    <CalendarHeader isPrinting={isPrinting} datesWidthPercentage={40} />
                  }
                </>
              )}
              {!isPrinting && <List
                component="div"
                disablePadding
                sx={{
                  width: "100%",
                  height: isPrinting ? "36px" : "49px",
                  maxHeight: isPrinting ? "36px" : "49px",
                  backgroundColor: "#e3e6ed",
                  borderLeft: `1px solid ${BORDER_COLOR}`,
                  borderRight: `1px solid ${BORDER_COLOR}`
                }}
              >
                <ListItemButton
                  onClick={() => handleWorkcenterToggle(item.data.id)}
                  sx={{
                    width: "100%",
                    height: isPrinting ? "36px" : "49px",
                    maxHeight: isPrinting ? "36px" : "49px",
                    gap: 0.5,
                    backgroundColor: "#e3e6ed",
                    "&:hover": {
                      backgroundColor: "#e3e6ed"
                    }
                  }}
                >
                  {!isPrinting && (
                    <>
                      {isWorkcenterOpen(item.data.id) ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </>
                  )}
                  <ListItemText>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        alignItems: "center"
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: isPrinting ? "14px" : "20px",
                          lineHeight: isPrinting ? "18px" : "160%",
                          fontWeight: 500,
                          letterSpacing: "0.15px",
                          color: "rgba(0, 0, 0, 0.87)"
                        }}
                      >
                        {item.data.name}
                      </Typography>
                      <Typography
                        component="label"
                        sx={{
                          color: "rgba(0, 0, 0, 0.87)",
                          fontSize: isPrinting ? "12px" : "13px",
                          fontWeight: "400",
                          lineHeight: "18px",
                          letterSpacing: "0.16px",
                          marginTop: isPrinting ? "2.5px" : "4px"
                        }}
                      >
                        {isPrinting
                          ? t(
                              item.data.totalWorkers === 1
                                ? "full-time-worker-scheduler_employee-count_singular"
                                : "full-time-worker-scheduler_employee-count",
                              { count: item.data.totalWorkers }
                            )
                          : t(
                              item.data.totalWorkers === 1
                                ? "full-time-worker-scheduler_worker-count_singular"
                                : "full-time-worker-scheduler_worker-count",
                              { count: item.data.totalWorkers }
                            )}
                      </Typography>
                    </Box>
                  </ListItemText>
                </ListItemButton>
              </List>}
            </Box>
          );
        }
        case "shiftpattern": {
          const workerCount = item.data.totalWorkers ?? item.data.workers.length ?? 0;
          return (
            <>
              {isPrinting && workerCount === 0 ? null : !isPrinting ? (
                <List
                  component="div"
                  disablePadding
                  sx={{
                    width: "100%",
                    height: isPrinting ? "36px" : "49px",
                    maxHeight: isPrinting ? "36px" : "49px",
                    backgroundColor: "#f4f8fc",
                    borderLeft: `1px solid ${BORDER_COLOR}`,
                    borderRight: `1px solid ${BORDER_COLOR}`
                  }}
                >
                  <ListItemButton
                    sx={{
                      pl: isPrinting ? 3 : 6,
                      gap: 0.5,
                      backgroundColor: "#f4f8fc",
                      height: isPrinting ? "36px" : "49px",
                      maxHeight: isPrinting ? "36px" : "49px",
                      width: "100%"
                    }}
                    onClick={() => handleShiftPatternToggle(item.id)}
                  >
                    {!isPrinting && (
                      <>
                        {isShiftPatternOpen(item.id) ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </>
                    )}
                    <ListItemText>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          alignItems: "center"
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: isPrinting ? "14px" : "20px",
                            lineHeight: isPrinting ? "18px" : "160%",
                            fontWeight: 500,
                            letterSpacing: "0.15px",
                            color: "rgba(0, 0, 0, 0.87)"
                          }}
                        >
                          {item.data.isFlex
                            ? t("full-time-worker-scheduler_flex-workers")
                            : item.data.isUnassigned
                              ? t("full-time-worker-scheduler_unknown-shift")
                              : shiftPatternsObj?.[item.data.id]?.name ?? "-"}
                        </Typography>
                        <Typography
                          component="label"
                          sx={{
                            color: "rgba(0, 0, 0, 0.87)",
                            fontSize: isPrinting ? "12px" : "13px",
                            fontWeight: "400",
                            lineHeight: "18px",
                            letterSpacing: "0.16px",
                            marginTop: isPrinting ? "2.5px" : "4px"
                          }}
                        >
                          {isPrinting
                            ? t(
                                workerCount === 1
                                  ? "full-time-worker-scheduler_employee-count_singular"
                                  : "full-time-worker-scheduler_employee-count",
                                { count: workerCount }
                              )
                            : t(
                                workerCount === 1
                                  ? "full-time-worker-scheduler_worker-count_singular"
                                  : "full-time-worker-scheduler_worker-count",
                                { count: workerCount }
                              )}
                        </Typography>
                      </Box>
                    </ListItemText>
                  </ListItemButton>
                </List>
              ) : <CalendarHeader
                isPrinting={isPrinting}
                borderLess={true}
                datesWidthPercentage={50}
                title={<Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    height: "100%",
                    width: "100%"
                  }}
                >
                  <Box sx={{width: "40%"}}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: isPrinting ? "14px" : "20px",
                        lineHeight: isPrinting ? "18px" : "160%",
                        fontWeight: 500,
                        letterSpacing: "0.15px",
                        color: "rgba(0, 0, 0, 0.87)"
                      }}
                    >
                      {item.data.isFlex
                        ? t("full-time-worker-scheduler_flex-workers")
                        : item.data.isUnassigned
                          ? t("full-time-worker-scheduler_unknown-shift")
                          : shiftPatternsObj?.[item.data.id]?.name ?? "-"}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: "20%",
                  }}>
                    <Typography
                      component="label"
                      sx={{
                        color: "rgba(0, 0, 0, 0.6)",
                        fontSize: "12px",
                        fontWeight: "400",
                        lineHeight: "18px",
                        letterSpacing: "0.16px",
                      }}>
                      {t("full-time-worker-scheduler_badge")}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: "40%"
                  }}>
                    <Typography
                      component="label"
                      sx={{
                        color: "rgba(0, 0, 0, 0.6)",
                        fontSize: "12px",
                        fontWeight: "400",
                        lineHeight: "18px",
                        letterSpacing: "0.16px",
                      }}>
                      {t("full-time-worker-scheduler_employee-name-count", {
                        count: workerCount,
                      })}
                    </Typography>
                  </Box>
                </Box>}
              />}
            </>
          );
        }
        case "jobgroup": {
          const jobWorkerCount = item.data.workers?.length || 0;
          if (isPrinting && jobWorkerCount === 0) {
            return null;
          }
          return (
            <Box
              sx={{
                width: "100%",
                height: isPrinting ? "32px" : "40px",
                maxHeight: isPrinting ? "32px" : "40px",
                borderLeft: isPrinting ? "none" : `1px solid ${BORDER_COLOR}`,
                borderRight: isPrinting ? "none" : `1px solid ${BORDER_COLOR}`,
                display: "flex",
                alignItems: "center",
                pl: isPrinting ? 8 : 6,
                boxSizing: "border-box",
                backgroundColor: isPrinting ? "rgba(0,0,0,0)" : "#fafafa",
                marginTop: isPrinting ? "16px" : "0px"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center"
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isPrinting ? "13px" : "16px",
                    lineHeight: isPrinting ? "16px" : "140%",
                    fontWeight: 500,
                    fontStyle:
                      item.data.name === "Unassigned" ? "italic" : "normal",
                    letterSpacing: "0.15px",
                    color: "rgba(0, 0, 0, 0.87)",
                    textDecoration: "underline"
                  }}
                >
                  {item.data.name}
                </Typography>
              </Box>
            </Box>
          );
        }
        case "worker": {
          return (
            <Box
              sx={{
                borderLeft: isPrinting ? "none" : `1px solid ${BORDER_COLOR}`,
                borderRight: isPrinting ? "none" : `1px solid ${BORDER_COLOR}`,
                borderBottom: isPrinting ? "none" : lastWorkerRow
                  ? `1px solid ${BORDER_COLOR}`
                  : "none", // Add bottom border if last worker
                width: "100%",
                marginBottom: isPrinting ? lastWorkerRow ? "16px" : 0 : 0
              }}
            >
              <WorkerRow workers={[item.data]} isPrinting={isPrinting} />
            </Box>
          );
        }
        case "no-workers": {
          if (isPrinting) return null;
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
                justifyContent: "center",
                height: "40px",
                borderLeft: `1px solid ${BORDER_COLOR}`, // Add borders for consistency
                borderRight: `1px solid ${BORDER_COLOR}`,
                borderBottom: `1px solid ${BORDER_COLOR}`
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Roboto",
                  fontStyle: "italic",
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "18px",
                  letterSpacing: "0.16px"
                }}
              >
                {t("full-time-worker-scheduler_no-workers-found")}
              </Typography>
            </Box>
          );
        }
        default:
          return null;
      }
    },
    [virtualizedItems, isPrinting, company, selectedLocationName, schedulingStatusesObj, startingDay, shiftPatternsObj, isWorkcenterOpen, handleWorkcenterToggle, isShiftPatternOpen, handleShiftPatternToggle, t]
  );
  // --- Rest of the component --- remains the same ---
  if (workcenters.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          gap: 2
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {t("full-time-worker-scheduler_no-workcenters-found")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("full-time-worker-scheduler_no-workcenters-description")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: `calc(${schedulerContainerHeight}px - ${
          isAppliedFilterInView ? "146px" : "89px"
        })`,
        overflow: isPrinting ? "visible" : "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%"
      }}
    >
      {/* Overlay when loading */}
      {updateAvailabilityStatusStatus === "pending" && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: `${schedulerContainerHeight}px`,
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        />
      )}
      {isPrinting ? (
        <Box className="print-target">
          {virtualizedItems.map((_, index) => (
            <Box
              className="no-break"
              sx={{
                width: "100%"
              }}
              // Use item.id which should now be sufficiently unique
              key={virtualizedItems[index].id}
            >
              {renderItem(index)}
            </Box>
          ))}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              flex: 1
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "12px",
                color: "rgba(0, 0, 0, 0.6)",
                textAlign: "right",
                marginTop: "16px",
                width: "100%"
              }}
            >
              {t("full-time-worker-scheduler_exported-by", {
                firstName,
                lastName,
                date: format(new Date(), "EEE, MMMM d yyyy h:mm a", {
                  locale: dateLocale,
                }),
              })}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Virtuoso
          style={{
            height: "100%",
            flex: 1
          }}
          totalCount={virtualizedItems.length}
          itemContent={renderItem}
          // Use item.id which should now be sufficiently unique
          computeItemKey={(index) => virtualizedItems[index].id}
          overscan={10}
          components={{
            Scroller: VirtuosoScroller
          }}
        />
      )}
    </Box>
  );
};

export default WorkcenterShiftpatternView;
